import { useState, useEffect } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import { X, AlertTriangle, Calculator, History, Calendar } from 'lucide-react';

interface Purchase {
  id: string;
  memberId: string;
  gymId: string;
  planId: string;
  startDate: string | null;
  endDate: string | null;
  status: string | null;
  purchaseDate: string;
  discountAmount: number | null;
  discountPercent: number | null;
  baseAmount: number | null;
  taxAmount: number | null;
  taxPercent: number | null;
  totalAmount: number | null;
  notes: string | null;
  invoiceId: string | null;
  invoiceGstin?: string | null;
  documentType?: string;
  member: {
    id: string;
    fullName: string | null;
    displayName: string | null;
  };
  plan: {
    id: string;
    name: string;
    price: number | null;
    durationMonths: number | null;
  };
  gym: {
    id: string;
    name: string;
    displayName: string;
    gstin: string | null;
    includeGstinInInvoices?: boolean;
    registeredAddress: string | null;
  };
}

interface EditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchase: Purchase | null;
  onPurchaseUpdated: () => void;
}

export default function EditPurchaseModal({ isOpen, onClose, purchase, onPurchaseUpdated }: EditPurchaseModalProps) {
  const [loading, setLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const [formData, setFormData] = useState({
    startDate: '',
    discountAmount: '0',
    discountPercent: '0',
    taxPercent: '18',
    notes: ''
  });

  // Populate form when purchase changes
  useEffect(() => {
    if (purchase && isOpen) {
      setFormData({
        startDate: purchase.startDate ? purchase.startDate.split('T')[0] : '',
        discountAmount: purchase.discountAmount?.toString() || '0',
        discountPercent: purchase.discountPercent?.toString() || '0',
        taxPercent: purchase.taxPercent?.toString() || '18',
        notes: purchase.notes || ''
      });
    }
  }, [purchase, isOpen]);

  // Calculate end date based on start date and plan duration
  const calculateEndDate = (startDate: string, durationMonths: number | null): string => {
    if (!startDate || !durationMonths) return '';
    const start = new Date(startDate);
    const end = new Date(start);
    end.setMonth(end.getMonth() + durationMonths);
    return end.toISOString().split('T')[0];
  };

  // Calculate amounts
  const baseAmount = Number(purchase?.plan?.price || 0);
  const discountAmount = parseFloat(formData.discountAmount) || 0;
  const discountPercent = parseFloat(formData.discountPercent) || 0;

  // Apply discount (synced in handleInputChange)
  const totalDiscount = discountAmount;
  const subtotal = Math.max(0, baseAmount - totalDiscount);

  // Calculate GST
  const taxPercent = parseFloat(formData.taxPercent) || 0;
  const taxAmount = (subtotal * taxPercent) / 100;
  const totalAmount = subtotal + taxAmount;

  const endDate = formData.startDate && purchase?.plan?.durationMonths
    ? calculateEndDate(formData.startDate, purchase.plan.durationMonths)
    : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!purchase) return;

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Not authenticated');
        return;
      }

      const payload = {
        startDate: formData.startDate,
        endDate: endDate,
        discountAmount: discountAmount,
        discountPercent: discountPercent,
        baseAmount: baseAmount,
        taxPercent: taxPercent,
        taxAmount: taxAmount,
        totalAmount: totalAmount,
        notes: formData.notes
      };

      const response = await fetch(`${API_BASE_URL}/api/purchases/${purchase.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Purchase updated successfully');
        onPurchaseUpdated();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update purchase');
      }
    } catch (error) {
      console.error('Error updating purchase:', error);
      toast.error('Failed to update purchase');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPurchase = async () => {
    if (!purchase) return;

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Not authenticated');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/purchases/${purchase.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ status: 'cancelled' })
      });

      if (response.ok) {
        toast.success('Purchase cancelled successfully');
        onPurchaseUpdated();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to cancel purchase');
      }
    } catch (error) {
      console.error('Error cancelling purchase:', error);
      toast.error('Failed to cancel purchase');
    } finally {
      setLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'discountAmount') {
      const amount = parseFloat(value) || 0;
      const percent = baseAmount > 0 ? (amount / baseAmount) * 100 : 0;
      setFormData(prev => ({
        ...prev,
        discountAmount: value,
        discountPercent: percent > 0 ? percent.toFixed(2).replace(/\.00$/, '') : '0'
      }));
    } else if (name === 'discountPercent') {
      const percent = parseFloat(value) || 0;
      const amount = (baseAmount * percent) / 100;
      setFormData(prev => ({
        ...prev,
        discountPercent: value,
        discountAmount: amount > 0 ? amount.toFixed(2).replace(/\.00$/, '') : '0'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  if (!isOpen || !purchase) return null;

  const isActive = purchase.status === 'active';

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1000] p-0 sm:p-4 animate-in fade-in duration-200" {...backdropClose(onClose)}>
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90dvh] overflow-hidden flex flex-col animate-in slide-in-from-bottom sm:slide-in-from-top-4 duration-300" onClick={(e) => e.stopPropagation()}>
        {/* Header - Premium Gradient */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-8 text-white relative shadow-lg shrink-0">
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-inner">
                <Calculator className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-black tracking-tight leading-none">Edit Purchase</h2>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-[10px] font-black uppercase tracking-widest bg-white/20 px-2 py-0.5 rounded-full border border-white/10">
                    {purchase.status || 'Draft'}
                  </span>
                  <p className="text-blue-100 text-sm font-bold opacity-80">Invoice #{purchase.invoiceId || purchase.id.slice(0, 8)}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isActive && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="hidden md:flex items-center gap-2 px-6 py-2.5 bg-red-500/10 hover:bg-red-500 text-white rounded-xl transition-all border border-red-500/20 backdrop-blur-sm font-bold text-sm active:scale-95"
                >
                  <X className="w-4 h-4" />
                  Cancel Purchase
                </button>
              )}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 md:relative md:top-0 md:right-0 p-2.5 bg-white/10 hover:bg-red-500 rounded-xl transition-all border border-white/20 backdrop-blur-sm active:scale-95 z-10"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10 px-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 text-red-600 mb-4">
                <AlertTriangle className="w-6 h-6" />
                <h3 className="text-lg font-bold">Cancel Purchase?</h3>
              </div>
              <p className="text-gray-600 mb-6">
                This will cancel the purchase and remove the membership for <strong>{purchase.member.displayName || purchase.member.fullName}</strong>. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Keep Purchase
                </button>
                <button
                  onClick={handleCancelPurchase}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Cancelling...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto bg-gray-50/30">
          <div className="p-8 space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left Column - Meta Data */}
              <div className="lg:col-span-5 space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm space-y-6">
                  <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    <History className="w-4 h-4 text-blue-600" />
                    Transaction Entities
                  </h3>

                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Billed From</p>
                      <p className="font-bold text-gray-900">{purchase.gym.name}</p>
                      {purchase.invoiceGstin && purchase.documentType !== 'bill_of_supply' && (
                        <p className="text-xs text-gray-500 font-medium mt-0.5">GST: {purchase.invoiceGstin}</p>
                      )}
                    </div>

                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Billed To</p>
                      <p className="font-bold text-gray-900">{purchase.member.displayName || purchase.member.fullName}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-600 rounded-2xl p-6 text-white shadow-lg shadow-blue-100 space-y-6 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
                    <Calendar className="w-32 h-32" />
                  </div>

                  <h3 className="text-xs font-black text-blue-100 uppercase tracking-widest relative">Selected Plan</h3>
                  <div className="relative">
                    <p className="text-2xl font-black tracking-tight">{purchase.plan.name}</p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <span className="px-3 py-1 bg-white/10 rounded-full text-xs font-bold border border-white/10">
                        {purchase.plan.durationMonths} Months
                      </span>
                      <span className="px-3 py-1 bg-white/10 rounded-full text-xs font-bold border border-white/10">
                        Base: ₹{baseAmount.toFixed(0)}
                      </span>
                    </div>
                  </div>
                </div>

                {isActive && (
                  <button
                    type="button"
                    onClick={() => setShowCancelConfirm(true)}
                    className="md:hidden w-full flex items-center justify-center gap-2 px-6 py-4 bg-red-50 text-red-600 rounded-2xl border border-red-100 font-bold active:scale-95 transition-all shadow-sm"
                  >
                    <X className="w-5 h-5" />
                    Cancel This Purchase
                  </button>
                )}
              </div>

              {/* Right Column - Financials & Logic */}
              <div className="lg:col-span-7 space-y-6">
                <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm space-y-8">
                  <h3 className="text-xl font-black text-gray-900 flex items-center gap-3">
                    <Calculator className="w-6 h-6 text-blue-600" />
                    Financial Configuration
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Activation Date</label>
                      <div className="relative">
                        <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="date"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white focus:border-blue-500 transition-all font-bold text-gray-900 shadow-inner"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Expiration Date</label>
                      <div className="relative">
                        <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={endDate ? new Date(endDate).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          }) : ''}
                          disabled
                          className="w-full pl-11 pr-4 py-3 bg-gray-100 border border-transparent rounded-xl font-bold text-gray-500 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Disc. Amount (₹)</label>
                      <input
                        type="number"
                        name="discountAmount"
                        value={formData.discountAmount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-900 shadow-inner"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Disc. Percent (%)</label>
                      <input
                        type="number"
                        name="discountPercent"
                        value={formData.discountPercent}
                        onChange={handleInputChange}
                        min="0"
                        max="100"
                        step="0.01"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-900 shadow-inner"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">GST Rate (%)</label>
                      <input
                        type="number"
                        name="taxPercent"
                        value={formData.taxPercent}
                        onChange={handleInputChange}
                        min="0"
                        max="100"
                        step="0.01"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-900 shadow-inner"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Observation Notes</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-medium text-gray-900 shadow-inner resize-none"
                      placeholder="Optional notes for this transaction..."
                    />
                  </div>

                  <div className="p-6 bg-gradient-to-br from-gray-900 to-blue-900 rounded-2xl text-white shadow-xl shadow-blue-100 space-y-4">
                    <h4 className="text-[10px] font-black text-blue-300 uppercase tracking-widest">Final Amount Calculation</h4>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
                      <div className="text-gray-400 font-bold">Base Price</div>
                      <div className="text-right font-black">₹{baseAmount.toFixed(2)}</div>

                      {totalDiscount > 0 && (
                        <>
                          <div className="text-red-400 font-bold">Total Discount ({discountPercent}%)</div>
                          <div className="text-right font-black text-red-400">- ₹{totalDiscount.toFixed(2)}</div>
                        </>
                      )}

                      <div className="text-gray-400 font-bold">Taxable Amount</div>
                      <div className="text-right font-black border-t border-white/10 pt-1">₹{subtotal.toFixed(2)}</div>

                      <div className="text-gray-400 font-bold">GST ({taxPercent}%)</div>
                      <div className="text-right font-black">₹{taxAmount.toFixed(2)}</div>

                      <div className="col-span-2 border-t-2 border-blue-500/30 my-1"></div>

                      <div className="text-lg font-black tracking-tight text-blue-300">Grand Total</div>
                      <div className="text-right text-2xl font-black tracking-tight text-white animate-in zoom-in-95 duration-300">
                        ₹{totalAmount.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer - Premium Glassmorphism */}
        <div className="p-6 bg-white/80 backdrop-blur-md border-t border-gray-100 flex justify-end gap-4 shrink-0 shadow-inner">
          <button
            type="button"
            onClick={onClose}
            className="px-8 py-3 bg-white border border-gray-200 text-gray-700 rounded-2xl font-black text-sm hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
          >
            Discard Changes
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-10 py-3 bg-blue-600 text-white rounded-2xl font-black text-sm hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-200 flex items-center gap-3 disabled:opacity-50"
          >
            {loading && (
              <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            <span>{loading ? 'Processing...' : 'Save Updates'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
